import { Box, Button, OSKIcon, OSKThemeType, Spinner, Typography, useRefState } from 'oskcomponents';
import { OSKView, NavComponent, noop, OrdersAPI, useProfileMetadata } from 'oskcore';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import ActiveModeSwitcher from '~/organisms/ActiveModeSwitcher';
import AppShimmerBar from '~/organisms/AppShimmerBar';
import { SigmaNavItems } from '~/organisms/SigmaNavItems';
import { RootState } from '~/redux/store';

import OrdersAdminTable from './table';
import OrdersAdminPaginator from './paginator';
import { SHORT_VISIBLE_PAGE_SIZE } from '~/pagination';
import { TaskingOrder } from 'oskcore/src/api/orders';
import { useTheme } from 'styled-components';

type OrdersAdminViewProps = {};

export type PendingOrderStatusEdit = {
    id: string;
    status: string;
};

const OrdersAdminView = ({ ...props }: OrdersAdminViewProps) => {
    const theme = useTheme() as OSKThemeType;
    const userProfile = useProfileMetadata()[0];

    const [taskingOrders, setTaskingOrders] = useState<TaskingOrder[]>([]);
    const [totalOrders, setTotalOrders] = useState<number | undefined>();
    const [currentPage, setCurrentPage] = useState<number | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [getPendingEdits, setPendingEdits] = useRefState<PendingOrderStatusEdit[]>([]);

    useEffect(() => {
        setIsLoading(true);
        /* Warning: Passing in an explicit value for 'next' is not how the API
        was designed and we assume all responsibility for calculating the item index
        ourselves instead of loading the 'next' href in `links`. */
        OrdersAPI.getOrders({
            limit: SHORT_VISIBLE_PAGE_SIZE,
            next: (currentPage ?? 0) * SHORT_VISIBLE_PAGE_SIZE,
        })
            .then((response) => {
                if (response.features?.length > 0) {
                    setTotalOrders(response.numberMatched);
                    setTaskingOrders(response.features);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage]);

    return (
        <OSKView>
            <AppShimmerBar />
            <Box style={{ flexGrow: 1 }}>
                <ActiveModeSwitcher />
                <NavComponent>
                    <SigmaNavItems />
                </NavComponent>
                <Box grow style={{ backgroundColor: 'white', color: 'black', padding: '20px 40px' }}>
                    <Box col grow>
                        <Typography variant="heading2">Order Operations</Typography>
                        <Box style={{ justifyContent: 'flex-end' }}>
                            {isEditing ? (
                                <>
                                    <Button
                                        onClick={() => {
                                            setIsEditing(false);

                                            getPendingEdits().forEach((edit) => {
                                                OrdersAPI.updateOrderStatus(edit.id, edit.status, userProfile.email);
                                            });

                                            setPendingEdits([]);
                                        }}
                                        style={{
                                            backgroundColor: theme.colors.green500,
                                        }}
                                    >
                                        <Box center="all">
                                            <OSKIcon code="save" mr={4} />
                                            <Typography variant="caption2" strong>
                                                Save Changes
                                            </Typography>
                                        </Box>
                                    </Button>
                                    <Button
                                        style={{ marginLeft: '8px', backgroundColor: theme.colors.black600 }}
                                        onClick={() => {
                                            setIsEditing(false);
                                            setPendingEdits([]);
                                        }}
                                    >
                                        <Box center="all">
                                            <OSKIcon code="times" mr={4} />
                                            <Typography variant="caption2" strong>
                                                Cancel
                                            </Typography>
                                        </Box>
                                    </Button>
                                </>
                            ) : (
                                <Button variant="action" onClick={() => setIsEditing(true)}>
                                    <Box center="all">
                                        <OSKIcon code="pencil" mr={4} />
                                        <Typography variant="caption2" strong>
                                            Edit Statuses
                                        </Typography>
                                    </Box>
                                </Button>
                            )}
                        </Box>
                        {isLoading ? (
                            <Box pt={100} center="all">
                                <Spinner variant="Box" size="Large" />
                            </Box>
                        ) : (
                            <OrdersAdminTable
                                orders={taskingOrders}
                                enableEdit={isEditing}
                                pendingEdits={getPendingEdits()}
                                onStatusChange={(id, status) => setPendingEdits([...getPendingEdits(), { id, status }])}
                            />
                        )}
                        <OrdersAdminPaginator
                            totalOrders={totalOrders}
                            currentPage={currentPage}
                            changePage={(pageNumber: number) => {
                                setCurrentPage(pageNumber);
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        </OSKView>
    );
};

const mapStateToProps = (state: RootState): OrdersAdminViewProps => {
    return {};
};

export default connect(mapStateToProps, noop)(OrdersAdminView);
